import {
  findVariant,
  initialSelection,
  selectValue,
  sellableVariants,
  toInt,
  variantStock,
  type CheckoutProduct,
  type OptionSelection,
  type ProductVariant,
} from "./variants";

/** A brand link resolved into the products its express cart starts with. */
export interface CheckoutLink {
  id: string;
  code: string;
  name: string | null;
  retailer_id: string;
  retailer_name: string;
  /** Chat link back to the brand on WhatsApp; null hides the chat button. */
  whatsapp_url: string | null;
  products: CheckoutProduct[];
  /**
   * Products that stay on the page at quantity 0 (greyed out): the link's own
   * products. Others (cart items from elsewhere) disappear at 0. Omitted means
   * every product stays.
   */
  pinnedProductIds?: string[];
}

/**
 * One cart line as the shopper edits it. The link supplies the products; the
 * shopper supplies the variant (via option choices) and quantity. A product
 * can have several lines (one per size/colour), keyed by `clientKey`, never by
 * product. `cartItemId` is separate and only populated from server cart rows.
 * The UI key stays local and is never sent to the API. The
 * session hook syncs the active product, variant and quantity to the API.
 *
 * Lines are never removed down to zero for a product: a product always keeps
 * at least one line, and a line at quantity 0 stays on the page (greyed out)
 * so it can be added back.
 */
export interface CheckoutLine {
  clientKey: string;
  cartItemId?: string;
  productId: string;
  selection: OptionSelection;
  quantity: number;
}

export const MAX_QUANTITY_PER_LINE = 10;

/** Creates a stable local key from a caller-owned counter (not a database ID). */
export function nextLocalLineKey(counter: { current: number }): string {
  counter.current += 1;
  return `local:${counter.current}`;
}

export interface ResolvedLine {
  product: CheckoutProduct;
  variant: ProductVariant | null;
  /** True when the product has variants and the shopper hasn't finished choosing. */
  needsVariant: boolean;
  /** Quantity 0: kept on the page but not part of the order. */
  inactive: boolean;
  unitPriceCents: number;
  lineTotalCents: number;
  maxQuantity: number;
  inStock: boolean;
}

// function hasStock(product: CheckoutProduct): boolean {
//   const variants = sellableVariants(product);
//   if (variants.length > 0) return variants.some((v) => variantStock(v) > 0);
//   return product.track_inventory === false || (product.inventory_count ?? 0) > 0;
// }
/** One line per link product, starting at quantity 0. */
export function initialLines(link: CheckoutLink): CheckoutLine[] {
  return link.products.map((product) => ({
    // Deterministic across re-renders of the same link.
    clientKey: `initial:${product.id}`,
    productId: product.id,
    selection: initialSelection(product),
    quantity: 0,
    // quantity: hasStock(product) ? 1 : 0,
  }));
}

// Mirrors `get_cart`: variant price overrides product price; stock comes from
// the variant, otherwise from `inventory_count` (unless inventory isn't tracked).
export function resolveLine(product: CheckoutProduct, line: CheckoutLine): ResolvedLine {
  const hasVariants = sellableVariants(product).length > 0;
  const variant = hasVariants ? findVariant(product, line.selection) : null;
  const inactive = line.quantity === 0;
  const needsVariant = hasVariants && !variant;

  const unitPriceCents = toInt(variant?.price) ?? product.price_cents;

  let available: number;
  if (variant) available = variantStock(variant);
  else if (hasVariants) available = MAX_QUANTITY_PER_LINE;
  else if (product.track_inventory === false) available = MAX_QUANTITY_PER_LINE;
  else available = Math.max(0, product.inventory_count ?? 0);

  return {
    product,
    variant,
    needsVariant,
    inactive,
    unitPriceCents,
    lineTotalCents: unitPriceCents * line.quantity,
    maxQuantity: Math.min(MAX_QUANTITY_PER_LINE, available),
    inStock: inactive || needsVariant || available >= line.quantity,
  };
}

/** Keeps quantity within 0..maxQuantity. */
export function clampQuantity(quantity: number, maxQuantity: number): number {
  return Math.max(0, Math.min(quantity, Math.max(0, maxQuantity)));
}

function productOf(link: CheckoutLink, productId: string): CheckoutProduct | undefined {
  return link.products.find((p) => p.id === productId);
}

export function setLineQuantity(link: CheckoutLink, lines: CheckoutLine[], clientKey: string, quantity: number): CheckoutLine[] {
  return lines.map((line) => {
    if (line.clientKey !== clientKey) return line;
    const product = productOf(link, line.productId);
    if (!product) return line;
    return { ...line, quantity: clampQuantity(quantity, resolveLine(product, line).maxQuantity) };
  });
}

export interface SelectResult {
  lines: CheckoutLine[];
  /** Set when the change produced a variant another line already had. */
  mergedInto?: string;
}

/**
 * Applies an option choice to a line. If the line now resolves to a variant
 * another line of the same product already holds, the two are merged into the
 * existing line (quantities added, capped at stock) and the edited line is
 * dropped. This matches the `cart_items` unique index on product + variant.
 */
export function selectOnLine(
  link: CheckoutLink,
  lines: CheckoutLine[],
  clientKey: string,
  axisName: string,
  value: string,
): SelectResult {
  const line = lines.find((l) => l.clientKey === clientKey);
  const product = line && productOf(link, line.productId);
  if (!line || !product) return { lines };

  const selection = selectValue(product, line.selection, axisName, value);
  const variant = findVariant(product, selection);
  const duplicate = variant
    ? lines.find((l) => l.clientKey !== clientKey && l.productId === line.productId && findVariant(product, l.selection)?.sku === variant.sku)
    : undefined;

  if (duplicate) {
    const max = resolveLine(product, duplicate).maxQuantity;
    return {
      mergedInto: duplicate.clientKey,
      lines: lines
        .filter((l) => l.clientKey !== clientKey)
        .map((l) => (l.clientKey === duplicate.clientKey ? { ...l, quantity: clampQuantity(l.quantity + line.quantity, max) } : l)),
    };
  }

  return {
    lines: lines.map((l) => {
      if (l.clientKey !== clientKey) return l;
      const next = { ...l, selection };
      // A line the shopper is actively choosing for stays at 1+; a greyed-out line stays at 0.
      const max = resolveLine(product, next).maxQuantity;
      return { ...next, quantity: l.quantity === 0 ? 0 : Math.max(1, clampQuantity(l.quantity, max)) };
    }),
  };
}

/** True while at least one in-stock variant isn't on a line yet. */
export function canAddLine(link: CheckoutLink, lines: CheckoutLine[], productId: string): boolean {
  const product = productOf(link, productId);
  if (!product) return false;
  const variants = sellableVariants(product);
  if (variants.length < 2) return false;

  const productLines = lines.filter((l) => l.productId === productId);
  if (productLines.some((l) => !findVariant(product, l.selection))) return false;

  const taken = new Set(productLines.map((l) => findVariant(product, l.selection)?.sku));
  return variants.some((v) => variantStock(v) > 0 && !taken.has(v.sku));
}

/** Adds an empty line for another size/colour, directly after the product's other lines. */
export function addLine(lines: CheckoutLine[], productId: string, clientKey: string): CheckoutLine[] {
  const newLine: CheckoutLine = { clientKey, productId, selection: {}, quantity: 1 };
  const lastIndex = lines.map((l) => l.productId).lastIndexOf(productId);
  return [...lines.slice(0, lastIndex + 1), newLine, ...lines.slice(lastIndex + 1)];
}

/** Only extra lines can be removed; a product always keeps at least one line. */
export function canRemoveLine(lines: CheckoutLine[], clientKey: string): boolean {
  const line = lines.find((l) => l.clientKey === clientKey);
  return Boolean(line) && lines.filter((l) => l.productId === line!.productId).length > 1;
}

export function removeLine(lines: CheckoutLine[], clientKey: string): CheckoutLine[] {
  return canRemoveLine(lines, clientKey) ? lines.filter((l) => l.clientKey !== clientKey) : lines;
}

function savedSelection(value: unknown): OptionSelection {
  const selection: OptionSelection = {};
  if (!value || typeof value !== "object") return selection;
  for (const [axis, choice] of Object.entries(value)) {
    if (typeof choice === "string") selection[axis] = choice;
  }
  return selection;
}

/**
 * Rebuilds the lines saved on the shopper's express session against the link
 * as it is now. Saved data comes back from the server, so it is checked rather
 * than trusted: lines for products no longer on the link are dropped, option
 * values must be strings, quantities are re-clamped to current stock, lines
 * that resolve to the same variant are merged, and every product keeps a line.
 */
export function restoreLines(link: CheckoutLink, saved: unknown): CheckoutLine[] {
  if (!Array.isArray(saved)) return initialLines(link);

  const restored: CheckoutLine[] = [];
  for (const raw of saved) {
    if (!raw || typeof raw !== "object") continue;
    const { clientKey, cartItemId: rawCartItemId, productId, selection: rawSelection, quantity: rawQuantity } = raw as Record<string, unknown>;
    if (typeof clientKey !== "string" || typeof productId !== "string") continue;
    if (restored.some((l) => l.clientKey === clientKey)) continue;
    const product = productOf(link, productId);
    if (!product) continue;

    const selection = savedSelection(rawSelection);
    const draft: CheckoutLine = {
      clientKey,
      ...(typeof rawCartItemId === "string" ? { cartItemId: rawCartItemId } : {}),
      productId,
      selection,
      quantity: 0,
    };
    const requested = typeof rawQuantity === "number" && Number.isInteger(rawQuantity) ? rawQuantity : 0;
    const quantity = clampQuantity(requested, resolveLine(product, draft).maxQuantity);

    const sku = findVariant(product, selection)?.sku;
    const duplicate = sku
      ? restored.find((l) => l.productId === productId && findVariant(product, l.selection)?.sku === sku)
      : undefined;
    if (duplicate) {
      duplicate.quantity = clampQuantity(duplicate.quantity + quantity, resolveLine(product, duplicate).maxQuantity);
      continue;
    }
    restored.push({ ...draft, quantity });
  }

  const initial = initialLines(link);
  return link.products.flatMap((product) => {
    const lines = restored.filter((l) => l.productId === product.id);
    return lines.length > 0 ? lines : initial.filter((l) => l.productId === product.id);
  });
}

export interface CheckoutTotals {
  itemCount: number;
  subtotalCents: number;
  pendingVariantProducts: string[];
  outOfStockProducts: string[];
  canContinue: boolean;
}

function unique(titles: string[]): string[] {
  return [...new Set(titles)];
}

/** Only lines with quantity > 0 count towards the order and its blockers. */
export function getTotals(link: CheckoutLink, lines: CheckoutLine[]): CheckoutTotals {
  const active = lines
    .filter((line) => line.quantity > 0)
    .map((line) => {
      const product = productOf(link, line.productId);
      return product ? { line, r: resolveLine(product, line) } : null;
    })
    .filter((x): x is { line: CheckoutLine; r: ResolvedLine } => x !== null);

  const pendingVariantProducts = unique(active.filter(({ r }) => r.needsVariant).map(({ r }) => r.product.title));
  const outOfStockProducts = unique(active.filter(({ r }) => !r.inStock).map(({ r }) => r.product.title));

  return {
    itemCount: active.reduce((sum, { line }) => sum + line.quantity, 0),
    subtotalCents: active.reduce((sum, { r }) => sum + r.lineTotalCents, 0),
    pendingVariantProducts,
    outOfStockProducts,
    canContinue: active.length > 0 && pendingVariantProducts.length === 0 && outOfStockProducts.length === 0,
  };
}
